import {ConfigObjectType, useApiQuery} from '@wallaby-ui/core';
import {FreeSoloValue, isFreeSolo, NormalizedOption} from '@wallaby-ui/document';
import {sortBy} from 'lodash';
import {useEffect, useState} from 'react';

/* Generic hook for a raw-text HAI value-list field (surgery types, user-defined tags, ...).
   Unlike invasive devices, these fields have NO predefined enum: every value is a plain string, so
   options/values are plain strings with no predefined/userDefined split and nothing is translated.

   The backend `/values` endpoint already returns the unified visibility set (active default list plus
   values currently in use), sorted. We fetch it and expose it as autocomplete/filter options. New fields
   added later reuse this hook by passing their own `url`. */

/** Normalizes a freeSolo autocomplete selection (mix of plain strings and FreeSoloValue) to plain strings. */
export const normalizeFreeSoloStrings = (inputs: (string | FreeSoloValue)[] | undefined): string[] =>
  (inputs ?? []).map((input) => (isFreeSolo(input) ? input.freeSolo : input));

export type UseValueListFieldProps = {
  config: ConfigObjectType;
  /** The field's `/values` endpoint, e.g. config.service.url.surgeryTypeValues. */
  url: string;
  /* Optional display-label mapper. Most value lists are pure raw text (label === value), but a field may
     carry known predefined values that need translation (e.g. invasive devices: the value is an enum NAME
     like "INTUBATION" and should display its translated label, while free-text values render as-is). */
  getLabel?: (value: string) => string;
};

export function useValueListField({config: _config, url, getLabel}: UseValueListFieldProps) {
  const [values, setValues] = useState<string[]>([]);
  const [options, setOptions] = useState<NormalizedOption<string>[]>([]);

  const {isLoading, refetch} = useApiQuery<string[]>([url], null, {
    axiosConfig: {url},
    reactQueryOptions: {
      staleTime: 0,
      refetchOnMount: 'always',
      onSuccess: (data) => setValues(data ?? []),
      onError: () => setValues([]),
    },
  });

  useEffect(() => {
    // id/original stay the raw value (that is what is sent to the BE); only the label may be translated.
    const opts: NormalizedOption<string>[] = (values ?? []).map((value) => ({
      id: value,
      label: getLabel ? getLabel(value) : value,
      original: value,
    }));
    setOptions(sortBy(opts, [(v) => v.label]));
  }, [values, getLabel]);

  return {options, isLoading, refetch, normalizeFreeSoloStrings};
}
